import { TekstSchematu } from "./TekstSchematu";
import { DocumentLink } from "./DocumentLink";
import { sensitive } from "../lib/sensitive";
import { t } from "../i18n";
import { Icons } from "../lib/icons";
import type { AnsweredField, Datapoint, Locale } from "../types";

/** Odpowiedź już udzielona: widoczna, zablokowana, z krzyżykiem do poprawki. */
export function LockedField({
  field,
  locale,
  onClear,
  busy,
  fresh,
  recordId,
}: {
  field: AnsweredField;
  locale: Locale;
  onClear: () => void;
  busy: boolean;
  /** Odpowiedź, która właśnie usiadła — dostaje jednorazowe `potwierdzenie`. */
  fresh?: boolean;
  /** Sprawa pola podpisu — pozwala pobrać podpisany PDF także po przejściu dalej. */
  recordId?: string;
}) {
  if (field.type === "note") return <NoteCard datapoint={field} />;

  const badge =
    field.origin === "verified" ? "KYC" : field.origin === "system" ? "auto" : null;

  // Numery dokumentów i rachunków czyta się cyfra po cyfrze — stąd font o stałej szerokości.
  const monospace = ["pesel", "bank_account", "number"].includes(field.type)
    || ["dokument_numer", "pasport_numer", "nip", "dg_nip", "firma_zatrudniajaca_nip"].includes(field.key);

  return (
    <div className={`field${fresh ? " potwierdzenie" : ""}`}>
      <span className="label">{field.title}</span>
      <div className="locked">
        <span className={`locked__value${monospace ? " locked__value--mono" : ""}`} {...sensitive(field)}>
          {field.type === "simple_sign" ? t("signed", locale) : field.display || field.value}
        </span>
        {/* Podpisany dokument zostawał niedostępny w chwili przejścia dalej —
            człowiek nie mógł wrócić do tego, co przed chwilą podpisał. */}
        {recordId && (
          <DocumentLink recordId={recordId} docKey={field.key} locale={locale} className="locked__doc" />
        )}
        {badge && (
          <span className={`badge badge--${field.origin}`} title={field.origin}>
            {badge}
          </span>
        )}
        <button
          className="icon-btn"
          onClick={onClear}
          disabled={busy}
          aria-label={`${t("clear", locale)}: ${field.title}`}
        >
          <Icons.cross size={18} />
        </button>
      </div>
    </div>
  );
}

/** Nota informacyjna albo koniec ścieżki. */
export function NoteCard({ datapoint }: { datapoint: Datapoint }) {
  const final = datapoint.severity === "final";
  const waiting = datapoint.key.startsWith("zamrazarka");

  const tone = final
    ? datapoint.blocking
      ? " note--danger"
      : " note--ok"
    : waiting
      ? " note--warn"
      : " note--info";

  return (
    <div className="field">
      <div className={`note${tone}`} role={datapoint.blocking ? "alert" : undefined}>
        <p className="note__title">{datapoint.title}</p>
        {datapoint.subtitle && <TekstSchematu tekst={datapoint.subtitle} className="note__text" />}
      </div>
    </div>
  );
}
